import React, { useEffect, useMemo, useState } from 'react';
import { useLanguage } from '../contexts/LanguageContext';
import { useFilterRailSlotSurface } from './mmec/ProToolFilterSlot';
import { DataMode } from '../types/DataMode';
import { MoneynessC, getMoneynessCDisplayName, getMoneynessCDisplayNameArabic } from '../utils/moneynessCUtils';
import { isStablePrefetchFilterLock, readStablePrimarySessionFirst } from '../utils/stableTabStorage';

export interface StandardFilterBarProps {
  selectedUnderlying: string;
  selectedExpiry: string;
  selectedSide?: string;
  selectedMoneyness?: string;
  selectedMoneynessC?: MoneynessC | 'all';
  selectedDataMode: DataMode;
  availableUnderlyings: string[];
  availableExpiries: string[];
  availableSides?: string[];
  availableMoneyness?: string[];
  availableMoneynessC?: MoneynessC[];
  onUnderlyingChange: (value: string) => void;
  onExpiryChange: (value: string) => void;
  onSideChange?: (value: string) => void;
  onMoneynessChange?: (value: string) => void;
  onMoneynessCChange?: (value: MoneynessC | 'all') => void;
  onDataModeChange: (value: DataMode) => void;
  showUnderlyingFilter?: boolean;
  showDataModeFilter?: boolean;
  showMoneynessCFilter?: boolean;
  compact?: boolean;
  /** Optional override; when inside ProToolFilterSlot portal, rail layout is applied automatically. */
  variant?: 'toolbar' | 'rail';
}

const StandardFilterBar: React.FC<StandardFilterBarProps> = ({
  selectedUnderlying,
  selectedExpiry,
  selectedSide = 'all',
  selectedMoneyness = 'all',
  selectedMoneynessC = 'all',
  selectedDataMode,
  availableUnderlyings,
  availableExpiries,
  availableSides = [],
  availableMoneyness = [],
  availableMoneynessC = [],
  onUnderlyingChange,
  onExpiryChange,
  onSideChange,
  onMoneynessChange,
  onMoneynessCChange,
  onDataModeChange,
  showUnderlyingFilter = true,
  showDataModeFilter = true,
  showMoneynessCFilter = false,
  compact = true,
  variant = 'toolbar',
}) => {
  const inFilterRail = useFilterRailSlotSurface();
  const effVariant = inFilterRail ? 'rail' : variant;
  const isRail = effVariant === 'rail';
  const compactSelectWidth = isRail ? '100%' : compact ? 104 : 138;

  const { language } = useLanguage();
  const [, setLockNonce] = useState(0);

  useEffect(() => {
    const bump = () => setLockNonce((n) => n + 1);
    window.addEventListener('dataSourceChanged', bump);
    window.addEventListener('mmecFuturesModeChanged', bump);
    return () => {
      window.removeEventListener('dataSourceChanged', bump);
      window.removeEventListener('mmecFuturesModeChanged', bump);
    };
  }, []);

  const stablePrefetchLock = isStablePrefetchFilterLock();
  const stablePrimary = readStablePrimarySessionFirst().trim();
  const underlyingReadout = stablePrefetchLock
    ? stablePrimary || selectedUnderlying || (language === 'ar' ? '—' : '—')
    : selectedUnderlying;

  const labelStyle = useMemo(
    (): React.CSSProperties => ({
      fontSize: isRail ? 11 : 10.5,
      fontWeight: 600,
      letterSpacing: '0.08em',
      textTransform: 'uppercase',
      color: 'var(--muted-2)',
      whiteSpace: isRail ? 'normal' : 'nowrap',
      marginRight: isRail ? 0 : compact ? 6 : 8,
      marginBottom: isRail ? 4 : 0,
      lineHeight: 1.2,
      fontFamily: 'var(--font-ui, "IBM Plex Sans", sans-serif)',
    }),
    [compact, isRail]
  );

  const selectStyle = (disabled?: boolean): React.CSSProperties => ({
    width: isRail ? '100%' : compactSelectWidth,
    minWidth: isRail ? 0 : compactSelectWidth,
    maxWidth: isRail ? '100%' : undefined,
    boxSizing: 'border-box',
    minHeight: isRail ? 32 : compact ? 28 : 32,
    fontSize: isRail ? 13 : 12,
    padding: isRail ? '7px 10px' : compact ? '0 8px' : '4px 8px',
    borderRadius: isRail ? 8 : 6,
    border: '1px solid var(--hair)',
    backgroundColor: disabled ? 'var(--base)' : 'var(--elev)',
    color: 'var(--txt)',
    outline: 'none',
    cursor: disabled ? 'not-allowed' : 'pointer',
    opacity: disabled ? 0.85 : 1,
    fontFamily: 'var(--font-ui, "IBM Plex Sans", sans-serif)',
  });

  const shellStyle: React.CSSProperties = isRail
    ? {
        width: '100%',
        boxSizing: 'border-box',
        padding: 0,
        marginBottom: 0,
        borderRadius: 0,
        border: 'none',
        backgroundColor: 'transparent',
      }
    : {
        padding: compact ? '6px 8px' : '12px',
        marginBottom: compact ? 8 : 12,
        borderRadius: 10,
        border: '1px solid var(--hair)',
        backgroundColor: 'var(--elev)',
      };

  const readoutStyle: React.CSSProperties = {
    ...selectStyle(true),
    display: 'flex',
    alignItems: 'center',
    minHeight: isRail ? 32 : compact ? 28 : 32,
    fontWeight: 600,
    fontFamily: 'var(--font-mono)',
    fontVariantNumeric: 'tabular-nums',
  };

  const hintStyle: React.CSSProperties = {
    marginTop: 4,
    fontSize: 11,
    lineHeight: 1.35,
    color: 'var(--muted)',
  };

  const showSideFilter = Boolean(onSideChange && availableSides.length > 0);
  const showMoneynessFilter = Boolean(onMoneynessChange && availableMoneyness.length > 0);
  const shouldShowMoneynessCFilter = Boolean(showMoneynessCFilter && onMoneynessCChange && availableMoneynessC.length > 0);

  const stableHint =
    language === 'ar'
      ? 'وضع Stable: الرمز والوضع الزمني مرتبطان بجلب السلسلة أعلاه. فعّل وضع العقود الآجلة لتغييرهما.'
      : 'Stable uses the fetched chain snapshot. Underlying and data mode follow the Stable bar. Turn on Futures mode to change them here.';

  const noFetchHint =
    language === 'ar'
      ? 'اضغط جلب في شريط Stable لاختيار الرمز.'
      : 'Use Fetch in the Stable bar to set the symbol.';

  const dataModeReadout =
    language === 'ar'
      ? 'لقطة السلسلة (تراكمي)'
      : 'Chain snapshot (accumulated)';

  const controlWrapStyle: React.CSSProperties = isRail
    ? {
        display: 'flex',
        flexDirection: 'column',
        alignItems: 'stretch',
        gap: 6,
        width: '100%',
        minHeight: 0,
      }
    : {
        display: 'inline-flex',
        alignItems: 'center',
        gap: compact ? 6 : 8,
        minHeight: compact ? 28 : 32,
      };

  return (
    <div style={shellStyle} dir={language === 'ar' ? 'rtl' : 'ltr'}>
      <div
        style={{
          display: 'flex',
          flexDirection: isRail ? 'column' : 'row',
          flexWrap: isRail ? 'nowrap' : 'wrap',
          alignItems: isRail ? 'stretch' : 'center',
          gap: isRail ? 14 : compact ? 8 : 12,
          width: isRail ? '100%' : undefined,
        }}
      >
        {showUnderlyingFilter && (
          <div style={controlWrapStyle}>
            <label style={labelStyle}>{language === 'ar' ? 'الأساس' : 'Underlying'}</label>
            {stablePrefetchLock ? (
              <>
                <div style={readoutStyle} title={stableHint}>
                  {underlyingReadout === 'all' || !underlyingReadout ? (language === 'ar' ? '—' : '—') : underlyingReadout}
                </div>
              </>
            ) : (
              <select
                style={selectStyle()}
                value={selectedUnderlying}
                onChange={(e) => onUnderlyingChange(e.target.value)}
                onFocus={(e) => {
                  e.currentTarget.style.boxShadow = '0 0 0 2px color-mix(in srgb, var(--acc) 35%, transparent)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <option value="all">{language === 'ar' ? 'الكل' : 'All'}</option>
                {availableUnderlyings.map((underlying) => (
                  <option key={underlying} value={underlying}>
                    {underlying}
                  </option>
                ))}
              </select>
            )}
          </div>
        )}

        <div style={controlWrapStyle}>
          <label style={labelStyle}>{language === 'ar' ? 'تاريخ الانتهاء' : 'Expiry'}</label>
          <select
            style={selectStyle()}
            value={selectedExpiry}
            onChange={(e) => onExpiryChange(e.target.value)}
            onFocus={(e) => {
              e.currentTarget.style.boxShadow = '0 0 0 2px color-mix(in srgb, var(--acc) 35%, transparent)';
            }}
            onBlur={(e) => {
              e.currentTarget.style.boxShadow = 'none';
            }}
          >
            <option value="all">{language === 'ar' ? 'الكل' : 'All'}</option>
            {availableExpiries.map((expiry) => (
              <option key={expiry} value={expiry}>
                {expiry}
              </option>
            ))}
          </select>
        </div>

        {showSideFilter && (
          <div style={controlWrapStyle}>
            <label style={labelStyle}>{language === 'ar' ? 'النوع' : 'Side'}</label>
            <select
              style={selectStyle()}
              value={selectedSide}
              onChange={(e) => onSideChange!(e.target.value)}
              onFocus={(e) => {
                e.currentTarget.style.boxShadow = '0 0 0 2px color-mix(in srgb, var(--acc) 35%, transparent)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <option value="all">{language === 'ar' ? 'الكل' : 'All'}</option>
              <option value="C">{language === 'ar' ? 'خيار شراء' : 'Call'}</option>
              <option value="P">{language === 'ar' ? 'خيار بيع' : 'Put'}</option>
              {availableSides
                .filter((side) => side !== 'C' && side !== 'P')
                .map((side) => (
                  <option key={side} value={side}>
                    {side}
                  </option>
                ))}
            </select>
          </div>
        )}

        {showMoneynessFilter && (
          <div style={controlWrapStyle}>
            <label style={labelStyle}>{language === 'ar' ? 'حالة الخيار' : 'Moneyness'}</label>
            <select
              style={selectStyle()}
              value={selectedMoneyness}
              onChange={(e) => onMoneynessChange!(e.target.value)}
              onFocus={(e) => {
                e.currentTarget.style.boxShadow = '0 0 0 2px color-mix(in srgb, var(--acc) 35%, transparent)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <option value="all">{language === 'ar' ? 'الكل' : 'All'}</option>
              <option value="ITM">{language === 'ar' ? 'في المال' : 'ITM (In The Money)'}</option>
              <option value="ATM">{language === 'ar' ? 'عند المال' : 'ATM (At The Money)'}</option>
              <option value="OTM">{language === 'ar' ? 'خارج المال' : 'OTM (Out The Money)'}</option>
              {availableMoneyness
                .filter((money) => !['ITM', 'ATM', 'OTM'].includes(money))
                .map((money) => (
                  <option key={money} value={money}>
                    {money}
                  </option>
                ))}
            </select>
          </div>
        )}

        {shouldShowMoneynessCFilter && (
          <div style={controlWrapStyle}>
            <label style={labelStyle}>{language === 'ar' ? 'حالة الخيار (محدث)' : 'MoneynessC'}</label>
            <select
              style={selectStyle()}
              value={selectedMoneynessC}
              onChange={(e) => onMoneynessCChange!(e.target.value as MoneynessC | 'all')}
              onFocus={(e) => {
                e.currentTarget.style.boxShadow = '0 0 0 2px color-mix(in srgb, var(--acc) 35%, transparent)';
              }}
              onBlur={(e) => {
                e.currentTarget.style.boxShadow = 'none';
              }}
            >
              <option value="all">{language === 'ar' ? 'الكل' : 'All'}</option>
              {availableMoneynessC.map((moneynessC) => (
                <option key={moneynessC} value={moneynessC}>
                  {language === 'ar' ? getMoneynessCDisplayNameArabic(moneynessC) : getMoneynessCDisplayName(moneynessC)}
                </option>
              ))}
            </select>
          </div>
        )}

        {showDataModeFilter &&
          (stablePrefetchLock ? (
            <div style={controlWrapStyle}>
              <label style={labelStyle}>{language === 'ar' ? 'وضع البيانات' : 'Data Mode'}</label>
              <div style={readoutStyle} title={stableHint}>
                {dataModeReadout}
              </div>
            </div>
          ) : (
            <div style={controlWrapStyle}>
              <label style={labelStyle}>{language === 'ar' ? 'وضع البيانات' : 'Data Mode'}</label>
              <select
                style={selectStyle()}
                value={selectedDataMode}
                onChange={(e) => onDataModeChange(e.target.value as DataMode)}
                onFocus={(e) => {
                  e.currentTarget.style.boxShadow = '0 0 0 2px color-mix(in srgb, var(--acc) 35%, transparent)';
                }}
                onBlur={(e) => {
                  e.currentTarget.style.boxShadow = 'none';
                }}
              >
                <option value="accumulated">{language === 'ar' ? 'التراكمي' : 'Accumulated'}</option>
                <option value="5min">{language === 'ar' ? '5 دقائق' : '5 Minutes'}</option>
              </select>
            </div>
          ))}
      </div>
      {stablePrefetchLock && showUnderlyingFilter && (
        <div style={hintStyle}>
          {(stablePrimary === '' && (selectedUnderlying === 'all' || !selectedUnderlying)) ? noFetchHint : stableHint}
        </div>
      )}
    </div>
  );
};

export default StandardFilterBar;
